import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FieldExpression, NodeExpression, toString as luceneToString } from 'lucene';

import { CLAIM_SERVER_URL } from '../../config';
import { PaginatedResult } from '../../shared/models/paginated-result';
import { FieldOfQualityIssue, QualityIssue } from '../models/quality-issue';
import { OrderBy } from '../../shared/utils/constant';
import { QualitySum } from '../models/quality-sum';
import { QualityIssueStatus } from '../models/all-quality-issue-constants';
import { DocamonResponse } from '../models/all-quality-issue-attachment';

const CLAIM_QUALITYISSUES_V2_URL = `${ CLAIM_SERVER_URL }/v2/qualityIssues`;

export const ISSUE_FIELDS: readonly FieldOfQualityIssue[] = <const> [
	'id',
	'steeluserId',
	'supplierIssueNumber',
	'batches.id',
	'batches.batchReference',
	'batches.qty',
	'batches.qtyUnit',
	'batches.claimedQty',
	'batches.claimedQtyUnit',
	'status',
	'issueType',
	'customerReference',
	'customerArticleReference',
	'customer',
	'customerDefect',
	'customerDefectDescription',
	'customerDefectDetailed',
	'customerDefectDetailedDescription',
	'supplierDefect',
	'supplierDefectDescription',
	'comment',
	'supplierComment',
	'supplierOrderNumber',
	'supplierOrderItemNumber',
	'customerOrderNumber',
	'customerOrderItemNumber',
	'qtyRequested',
	'weightUnitRequested',
	'amountRequested',
	'currencyRequested',
	'qtyAccepted',
	'amountAccepted',
	'currencyAccepted',
	'sampleTarget',
	'sampleTargetComment',
	'creationDate',
	'modificationDate',
	'creditNoteDate',
	'closedDate',
	'sampleTargetDescription',
	'wantVisit',
	'attachments',
	'origin',
	'otherCostAmount',
	'claimType'
];

@Injectable({ providedIn: 'root' })
export class QualityIssueService {
	static readonly ISSUE_TABLE = 'issue';

	constructor(
		private readonly http: HttpClient,
	) {}

	getDownloadFile(claimID: string, path: string, fileName: string) {
		return `${ CLAIM_QUALITYISSUES_V2_URL }/${ claimID }/attachment/${ path }/download/
			${ encodeURIComponent(fileName) }`;
	}

	getIssues(
		fields?: readonly FieldOfQualityIssue[],
		filter?: string,
		index?: number,
		limit?: number,
		orderBy?: OrderBy<keyof QualityIssue>[] | OrderBy<keyof QualityIssue>,
		distinct?: boolean,
		onlyCount?: boolean,
	) {
		const url = new URL(CLAIM_QUALITYISSUES_V2_URL);
		const params = url.searchParams;
		if (fields) {
			params.append('fields', fields.join(','));
		}
		// Filters
		if (filter) {
			params.append('filter', filter);
		}
		if (orderBy) {
			params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));
		}
		if (index !== undefined && limit !== undefined) {
			params.append('page', String(Math.floor(index / limit) + 1));
		}
		if (limit !== undefined) {
			params.append('limit', String(limit));
		}
		if (distinct) {
			params.append('distinct', String(distinct));
		}
		if (onlyCount) {
			params.append('onlyCount', String(onlyCount));
		}
		return this.http.get<PaginatedResult<QualityIssue>>(url.toString());
	}

	getIssuesCount(filter?: string) {
		return this.getIssues(
			undefined,
			filter,
			undefined,
			undefined,
			undefined,
			undefined,
			true,
		);
	}

	getClaimByID(claimId: string) {
		const fields = ISSUE_FIELDS;

		const url = new URL(`${ CLAIM_QUALITYISSUES_V2_URL }/${ claimId }`);
		const params = url.searchParams;
		params.append('fields', fields.join(','));

		return this.http.get<QualityIssue>(url.toString());
	}

	getIssuesSum(field: string, groupBy: string, filter?: string) {
		const url = new URL(`${ CLAIM_QUALITYISSUES_V2_URL }/sum`);
		const params = url.searchParams;
		params.append('field', field);
		params.append('groupBy', groupBy);
		if (filter) {
			params.append('filter', filter);
		}
		return this.http.get<PaginatedResult<QualitySum>>(url.toString());
	}

	getAllIssuesbyPendingStatusFilter() {
		const pending: FieldExpression = {
			field: 'status',
			term: QualityIssueStatus.PENDING,
			quoted: true
		};

		const created: FieldExpression = {
			field: 'status',
			term: 'claimstatus.created',
			quoted: true
		};

		const claimIssuePendingField: NodeExpression = {
			left: pending,
			operator: 'OR',
			right: created,
			parenthesized: true
		};

		return luceneToString(claimIssuePendingField);
	}

	getAllIssuesbyBatchReferenceFilter(claimReference: string) {
		const claimReferenceField: FieldExpression = {

			field: 'batches.batchReference',
			term: claimReference,
			quoted: true
		};
		return luceneToString(claimReferenceField);
	}

	uploadAttachment(file: File, hash: string) {
		const form = new FormData();
		form.append('file', file, file.name);
		return this.http.post<DocamonResponse>(
			`${ CLAIM_SERVER_URL }/v2/attachments/${ encodeURIComponent(hash) }`,
			form,
			{ responseType: 'json', reportProgress: true, observe: 'events' }
		);
	}
}
